import axios from 'axios';
import { getAccessToken, clearTokens } from '../context/AuthContext';
import { ensureFreshToken, tokenStore } from '../services/api';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: { 'Content-Type': 'application/json' },
  // Cloud Run scales to zero when idle and the free-tier Neon database
  // autosuspends too, so the first request after a quiet period can take
  // 20-40s just to cold-start both — 15s was cutting that off mid-wake.
  timeout: 45000,
});

/* ── Request interceptor ─────────────────────────────────────────────────── */

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/* ── Response interceptor (silent token refresh) ─────────────────────────── */

// Refresh is delegated to services/api.ts's ensureFreshToken(), which holds a
// single in-flight refresh promise shared across BOTH this axios client and the
// fetch-based client in services/api.ts. Without that shared mutex, concurrent
// 401s from the two independent HTTP clients each raced to refresh with the same
// (single-use, server-rotated) refresh token — the first call succeeded and
// rotated it, the second then got rejected as already-revoked and force-logged
// the user out even though the session was actually fine.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    if (error.response?.status !== 401 || original._retry) {
      return Promise.reject(error);
    }

    if (original.url?.includes('/auth/login') || original.url?.includes('/auth/refresh')) {
      return Promise.reject(error);
    }

    original._retry = true;
    const refreshed = await ensureFreshToken();
    if (!refreshed) {
      clearTokens();
      window.location.href = '/login';
      return Promise.reject(error);
    }

    const newToken = tokenStore.getAccess();
    if (newToken) original.headers.Authorization = `Bearer ${newToken}`;
    return api(original);
  },
);

export default api;
